import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../../lib/api';

export const SpeciesCarousel: React.FC = () => {
  const { data: speciesData, isLoading, error } = useQuery({
    queryKey: queryKeys.treeSpecies,
    queryFn: api.getTreeSpecies,
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center text-gray-500">Loading tree species...</div>
      </div>
    );
  }

  if (error) {
    console.error('Error in SpeciesCarousel:', error);
    return (
      <div className="space-y-6">
        <div className="text-center text-red-500">Failed to load tree species</div>
      </div>
    );
  }

  if (!speciesData || !Array.isArray(speciesData) || speciesData.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center text-gray-500">No tree species available</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <button className="px-4 py-2 bg-forest-100 text-forest-700 rounded-lg text-sm font-medium">
          All Species
        </button>
        <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
          Native
        </button>
        <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
          Fast Growing
        </button>
        <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
          Drought Tolerant
        </button>
      </div>

      {/* Species Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {speciesData.map((species) => (
          <div key={species.id} className="card hover:shadow-md transition-shadow duration-200">
            <div className="aspect-w-16 aspect-h-9 mb-4">
              <div className="w-full h-48 bg-forest-100 rounded-lg flex items-center justify-center">
                <svg className="w-16 h-16 text-forest-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{species.name}</h3>
                <p className="text-sm text-gray-500 italic">{species.scientific_name}</p>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Max Height:</span>
                <span className="font-medium">{species.max_height_feet} ft</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Growth Rate:</span>
                <span className={`
                  px-2 py-1 rounded text-xs font-medium capitalize
                  ${species.growth_rate === 'fast' ? 'bg-green-100 text-green-800' :
                    species.growth_rate === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }
                `}>
                  {species.growth_rate}
                </span>
              </div>
              
              <button className="btn-primary w-full">
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Load More */}
      <div className="text-center">
        <button className="btn-secondary">
          Load More Species
        </button>
      </div>
    </div>
  );
};
